/**
 * MKS-393 — the merch-size and personal-details API clients.
 *
 * Three surfaces, three guards:
 *
 *   - `/auth/me/personal-details` — self-scope; the backend route carries
 *     `[authenticate]` and NOT `impersonate`, so it always reads and writes
 *     the ACTING user's row. An admin acting as someone else gets their own
 *     details here, by design.
 *   - `/admin/users/:id/merch-sizes` — `requireHR` (admin + hr), writes the
 *     six merch columns and nothing else. `PUT /admin/users/:id` is untouched
 *     and stays admin-only.
 *   - `/hr/merch-sizes[/xlsx|/csv]` — `requireHR`, the overview and its
 *     exports. All three take the same `missingOnly` filter so a download
 *     matches the table it was taken from.
 *
 * Every field is optional on a write: an absent key is a no-op and `null`
 * clears the value.
 */
import { apiGet, apiPatch, apiPut, API_BASE_URL, getBaseAuthHeaders } from './client';
import type { MerchSizeField, MerchSizes } from '../../constants/merchSizes';

export interface PersonalDetails extends MerchSizes {
  /** `YYYY-MM-DD`, what `<input type="date">` round-trips. */
  birthDate: string | null;
}

export type PersonalDetailsPatch = Partial<PersonalDetails>;
export type MerchSizesPatch = Partial<MerchSizes>;

export interface MerchOverviewRow extends MerchSizes {
  userId: number;
  name: string;
  email: string;
  missing: MerchSizeField[];
}

export interface MerchSizeCount {
  value: string;
  count: number;
}

export interface MerchOverview {
  rows: MerchOverviewRow[];
  counts: Record<MerchSizeField, MerchSizeCount[]>;
  /** Headcount BEFORE `missingOnly` narrows the rows. */
  totalPeople: number;
}

export function fetchPersonalDetails(): Promise<{ data: PersonalDetails }> {
  return apiGet<{ data: PersonalDetails }>('/auth/me/personal-details');
}

export function updatePersonalDetails(
  patch: PersonalDetailsPatch,
): Promise<{ data: PersonalDetails }> {
  return apiPatch<{ data: PersonalDetails }>('/auth/me/personal-details', patch);
}

export function fetchUserMerchSizes(userId: number): Promise<{ data: MerchSizes }> {
  return apiGet<{ data: MerchSizes }>(`/admin/users/${userId}/merch-sizes`);
}

export function updateUserMerchSizes(
  userId: number,
  patch: MerchSizesPatch,
): Promise<{ data: MerchSizes }> {
  return apiPut<{ data: MerchSizes }>(`/admin/users/${userId}/merch-sizes`, patch);
}

export function fetchMerchOverview(missingOnly: boolean): Promise<{ data: MerchOverview }> {
  return apiGet<{ data: MerchOverview }>(
    `/hr/merch-sizes${missingOnly ? '?missingOnly=true' : ''}`,
  );
}

/**
 * Download an export. `apiGet` parses JSON, so the binary/CSV endpoints are
 * fetched directly with the same auth headers and handed to the browser as a
 * blob — the pattern the other report downloads in this app use.
 */
export async function downloadMerchExport(
  format: 'xlsx' | 'csv',
  missingOnly: boolean,
): Promise<{ blob: Blob; filename: string }> {
  const query = missingOnly ? '?missingOnly=true' : '';
  const response = await fetch(`${API_BASE_URL}/hr/merch-sizes/${format}${query}`, {
    method: 'GET',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to download the merch export: ${response.statusText}`);
  }

  // Prefer the server's own name, same as the invoice timesheet download.
  const disposition = response.headers.get('Content-Disposition');
  const ascii = disposition?.match(/filename="?([^";]+)"?/);

  return {
    blob: await response.blob(),
    filename: ascii?.[1] ?? `merch-sizes.${format}`,
  };
}
